import { Button, Tooltip } from '@heroui/react'
import { Maximize2, Pause, Play, Volume1, Volume2, VolumeX } from 'lucide-react'
import React, { useCallback, useEffect, useRef, useState } from 'react'

interface VideoPlayerProps {
  src: string
  className?: string
}

export default function VideoPlayer({ src, className = '' }: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const progressRef = useRef<HTMLInputElement>(null)
  const volumeSliderRef = useRef<HTMLInputElement>(null)

  // Video state
  const [isVideoPlaying, setIsVideoPlaying] = useState(false)
  const [isVideoMuted, setIsVideoMuted] = useState(false)
  const [videoProgress, setVideoProgress] = useState(0)
  const [videoDuration, setVideoDuration] = useState(0)
  const [showVideoControls, setShowVideoControls] = useState(true)
  const [isVideoReady, setIsVideoReady] = useState(false)
  const [volume, setVolume] = useState(1) // Volume from 0 to 1
  const [showVolumeSlider, setShowVolumeSlider] = useState(false)

  // Frame-loop + slider refs
  const rafIdRef = useRef<number | null>(null)
  const rvfcIdRef = useRef<number | null>(null)
  const lastLabelTsRef = useRef(0)
  const isScrubbingRef = useRef(false)

  // Video controls timeout
  const videoControlsTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const volumeSliderTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // ======= Smooth progress helpers =======
  const updateSlider = useCallback((t: number) => {
    const v = videoRef.current
    const el = progressRef.current

    if (!v || !el) return

    // Imperatively move the thumb (no React state)
    el.value = String(t)

    // Update filled track
    const pct = (t / (v.duration || 1)) * 100

    el.style.background = `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${pct}%, rgba(255,255,255,0.2) ${pct}%)`

    // Throttle label updates to ~5 fps
    const now = performance.now()

    if (now - lastLabelTsRef.current > 200) {
      setVideoProgress(t)
      lastLabelTsRef.current = now
    }
  }, [])

  const startProgressLoop = useCallback(() => {
    const vid = videoRef.current

    if (!vid) return

    // Prefer per-frame callback from the video decode pipeline
    const anyVid = vid as any

    if (typeof anyVid.requestVideoFrameCallback === 'function') {
      const tick = (_now: number, meta: { mediaTime: number }) => {
        if (!videoRef.current) return
        if (!isScrubbingRef.current) {
          updateSlider(meta?.mediaTime ?? videoRef.current.currentTime)
        }
        rvfcIdRef.current = anyVid.requestVideoFrameCallback(tick)
      }

      rvfcIdRef.current = anyVid.requestVideoFrameCallback(tick)

      return
    }

    // Fallback: rAF
    const loop = () => {
      if (!videoRef.current) return
      if (!isScrubbingRef.current) {
        updateSlider(videoRef.current.currentTime)
      }
      rafIdRef.current = requestAnimationFrame(loop)
    }

    rafIdRef.current = requestAnimationFrame(loop)
  }, [updateSlider])

  const stopProgressLoop = useCallback(() => {
    if (rafIdRef.current != null) {
      cancelAnimationFrame(rafIdRef.current)
      rafIdRef.current = null
    }
    const vid = videoRef.current as any

    if (rvfcIdRef.current != null && vid && typeof vid.cancelVideoFrameCallback === 'function') {
      vid.cancelVideoFrameCallback(rvfcIdRef.current)
      rvfcIdRef.current = null
    }
  }, [])

  // ===== Video controls =====
  const toggleVideoPlay = useCallback(() => {
    const v = videoRef.current

    if (!v) return

    if (isVideoPlaying) {
      v.pause()
      stopProgressLoop()
      setIsVideoPlaying(false)
    } else {
      v.play()
      startProgressLoop()
      setIsVideoPlaying(true)
    }
  }, [isVideoPlaying, startProgressLoop, stopProgressLoop])

  const toggleVideoMute = useCallback(() => {
    if (!videoRef.current) return

    if (isVideoMuted) {
      // Unmute and restore previous volume
      videoRef.current.muted = false
      videoRef.current.volume = volume
      setIsVideoMuted(false)
    } else {
      // Mute
      videoRef.current.muted = true
      setIsVideoMuted(true)
    }
  }, [isVideoMuted, volume])

  const handleVolumeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newVolume = parseFloat(e.target.value)

      setVolume(newVolume)

      if (videoRef.current) {
        videoRef.current.volume = newVolume
        // Unmute if volume is increased from 0
        if (newVolume > 0 && isVideoMuted) {
          videoRef.current.muted = false
          setIsVideoMuted(false)
        }
        // Mute if volume is set to 0
        if (newVolume === 0 && !isVideoMuted) {
          videoRef.current.muted = true
          setIsVideoMuted(true)
        }
      }
    },
    [isVideoMuted],
  )

  const handleVolumeSliderMouseEnter = useCallback(() => {
    setShowVolumeSlider(true)
    setShowVideoControls(true) // Ensure video controls are visible
    if (volumeSliderTimeoutRef.current) {
      clearTimeout(volumeSliderTimeoutRef.current)
    }
    // Clear video controls timeout so they don't disappear while volume slider is visible
    if (videoControlsTimeoutRef.current) {
      clearTimeout(videoControlsTimeoutRef.current)
    }
  }, [])

  const handleVolumeSliderMouseLeave = useCallback(() => {
    volumeSliderTimeoutRef.current = setTimeout(() => {
      setShowVolumeSlider(false)
      // After hiding volume slider, check if we should also hide video controls
      if (videoControlsTimeoutRef.current) {
        clearTimeout(videoControlsTimeoutRef.current)
        videoControlsTimeoutRef.current = setTimeout(() => {
          setShowVideoControls(false)
        }, 2000)
      }
    }, 1000)
  }, [])

  const getVolumeIcon = () => {
    if (isVideoMuted || volume === 0) return <VolumeX className="h-4 w-4" />
    if (volume < 0.5) return <Volume1 className="h-4 w-4" />

    return <Volume2 className="h-4 w-4" />
  }

  const handleVideoSeek = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!videoRef.current) return
      const time = parseFloat(e.target.value)

      videoRef.current.currentTime = time
      updateSlider(time)
    },
    [updateSlider],
  )

  const handleVideoEnded = useCallback(() => {
    setIsVideoPlaying(false)
    setVideoProgress(0)
    if (videoRef.current) {
      videoRef.current.currentTime = 0
    }
    updateSlider(0)
    stopProgressLoop()
  }, [stopProgressLoop, updateSlider])

  const handleVideoMouseMove = useCallback(() => {
    setShowVideoControls(true)
    if (videoControlsTimeoutRef.current) {
      clearTimeout(videoControlsTimeoutRef.current)
    }
    videoControlsTimeoutRef.current = setTimeout(() => {
      // Only hide video controls if volume slider is not visible
      if (!showVolumeSlider) {
        setShowVideoControls(false)
      }
    }, 3000)
  }, [showVolumeSlider])

  const toggleFullscreen = useCallback(() => {
    if (!videoRef.current) return
    if (document.fullscreenElement) {
      document.exitFullscreen()
    } else {
      videoRef.current.requestFullscreen()
    }
  }, [])

  const handleVideoLoadedData = useCallback(() => {
    const v = videoRef.current

    if (!v) return
    setIsVideoReady(true)
    setVideoDuration(v.duration || 0)
    updateSlider(v.currentTime || 0)
  }, [updateSlider])

  // ===== Page Visibility API =====
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && isVideoPlaying && videoRef.current) {
        // Pause video when tab becomes hidden
        videoRef.current.pause()
        setIsVideoPlaying(false)
        stopProgressLoop()
      } else if (!document.hidden && !isVideoPlaying && videoRef.current) {
        // Resume video when tab becomes visible (only if it was playing before)
        videoRef.current.play()
        setIsVideoPlaying(true)
        startProgressLoop()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [isVideoPlaying, stopProgressLoop, startProgressLoop])

  // ===== Keyboard =====
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Video controls
      if (e.key === ' ') {
        e.preventDefault()
        toggleVideoPlay()

        return
      }
      if (e.key === 'm') {
        e.preventDefault()
        toggleVideoMute()

        return
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        if (videoRef.current) {
          videoRef.current.currentTime = Math.max(0, videoRef.current.currentTime - 10)
          updateSlider(videoRef.current.currentTime)
        }

        return
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault()
        if (videoRef.current) {
          videoRef.current.currentTime = Math.min(
            videoRef.current.duration,
            videoRef.current.currentTime + 10,
          )
          updateSlider(videoRef.current.currentTime)
        }

        return
      }
      if (e.key === 'f' || e.key === 'F') {
        e.preventDefault()
        toggleFullscreen()

        return
      }
      // Volume controls
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        const newVolume = Math.min(1, volume + 0.1)

        setVolume(newVolume)
        if (videoRef.current) {
          videoRef.current.volume = newVolume
          if (newVolume > 0 && isVideoMuted) {
            videoRef.current.muted = false
            setIsVideoMuted(false)
          }
        }

        return
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        const newVolume = Math.max(0, volume - 0.1)

        setVolume(newVolume)
        if (videoRef.current) {
          videoRef.current.volume = newVolume
          if (newVolume === 0 && !isVideoMuted) {
            videoRef.current.muted = true
            setIsVideoMuted(true)
          }
        }

        return
      }
    }

    document.addEventListener('keydown', onKey)

    return () => document.removeEventListener('keydown', onKey)
  }, [toggleFullscreen, toggleVideoMute, toggleVideoPlay, updateSlider, volume, isVideoMuted])

  // ===== Cleanup =====
  useEffect(() => {
    return () => {
      if (videoControlsTimeoutRef.current) {
        clearTimeout(videoControlsTimeoutRef.current)
      }
      if (volumeSliderTimeoutRef.current) {
        clearTimeout(volumeSliderTimeoutRef.current)
      }
      stopProgressLoop()
    }
  }, [stopProgressLoop])

  // Format time for video progress
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)

    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  return (
    <div className={`relative w-full h-full flex items-center justify-center p-8 ${className}`}>
      <div className="relative max-w-[1360px] max-h-[763px] w-full h-full flex items-center justify-center video-container">
        <div className="relative">
          <Tooltip content="Click to play/pause" placement="top">
            <video
              ref={videoRef}
              className="max-w-full max-h-full object-contain cursor-pointer"
              controls={false}
              onCanPlay={handleVideoLoadedData}
              onEnded={handleVideoEnded}
              onLoadedMetadata={handleVideoLoadedData}
              onMouseMove={handleVideoMouseMove}
              onClick={e => {
                e.stopPropagation()
                toggleVideoPlay()
              }}
              onPause={() => {
                setIsVideoPlaying(false)
                stopProgressLoop()
              }}
              onPlay={() => {
                setIsVideoPlaying(true)
                startProgressLoop()
              }}
            >
              <source src={src} />
              Your browser does not support the video tag.
            </video>
          </Tooltip>

          {/* Video Controls Overlay */}
          {isVideoReady ? (
            <div
              className={`
                absolute w-full bottom-0 transition-opacity duration-300
                ${showVideoControls ? 'opacity-100' : 'opacity-0'}
              `}
            >
              {/* Bottom progress bar */}
              <div className="p-2">
                <div className="bg-black/60 backdrop-blur-sm rounded-lg p-3">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-white text-sm font-medium min-w-8">
                      {formatTime(videoProgress)}
                    </span>
                    <div className="flex-1">
                      <input
                        ref={progressRef}
                        className="w-full h-2 bg-white/20 rounded-lg appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                        defaultValue={0}
                        max={videoDuration || 0}
                        min="0"
                        step="any"
                        type="range"
                        onChange={handleVideoSeek}
                        onInput={handleVideoSeek}
                        onMouseDown={() => {
                          isScrubbingRef.current = true
                        }}
                        onMouseUp={() => {
                          isScrubbingRef.current = false
                        }}
                        onTouchEnd={() => {
                          isScrubbingRef.current = false
                        }}
                        onTouchStart={() => {
                          isScrubbingRef.current = true
                        }}
                      />
                    </div>
                    <span className="text-white text-sm font-medium">
                      {formatTime(videoDuration)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-2">
                    <div>
                      <Tooltip content="Play/Pause (Space)" placement="bottom">
                        <Button
                          isIconOnly
                          aria-label={isVideoPlaying ? 'Pause' : 'Play'}
                          className="bg-black/60 text-white w-full h-full px-10 py-4 rounded-full"
                          variant="light"
                          onPress={toggleVideoPlay}
                        >
                          {isVideoPlaying ? (
                            <Pause className="h-5 w-5" />
                          ) : (
                            <Play className="h-5 w-5" />
                          )}
                        </Button>
                      </Tooltip>
                    </div>
                    <div className="flex items-center gap-2 bg-black/60 backdrop-blur-md rounded-full px-4 py-2">
                      <div className="relative flex items-center">
                        <Tooltip content="Mute/Unmute (M)" placement="bottom">
                          <Button
                            isIconOnly
                            aria-label={isVideoMuted ? 'Unmute' : 'Mute'}
                            className="text-white hover:bg-white/10"
                            size="sm"
                            variant="light"
                            onMouseEnter={handleVolumeSliderMouseEnter}
                            onPress={toggleVideoMute}
                          >
                            {getVolumeIcon()}
                          </Button>
                        </Tooltip>

                        {/* Volume Slider */}
                        {showVolumeSlider && (
                          <div
                            className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 bg-black/60 backdrop-blur-sm rounded-lg p-3 shadow-lg border border-white/10"
                            role="presentation"
                            onMouseEnter={handleVolumeSliderMouseEnter}
                            onMouseLeave={handleVolumeSliderMouseLeave}
                          >
                            <div className="w-8 h-24 flex flex-col items-center justify-center gap-2">
                              <input
                                ref={volumeSliderRef}
                                className="w-24 h-2 bg-white/20 rounded-lg appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transform -rotate-90 origin-center"
                                max="1"
                                min="0"
                                step="0.01"
                                type="range"
                                value={isVideoMuted ? 0 : volume}
                                style={{
                                  background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${(isVideoMuted ? 0 : volume) * 100}%, rgba(255,255,255,0.2) ${(isVideoMuted ? 0 : volume) * 100}%)`,
                                }}
                                onChange={handleVolumeChange}
                                onInput={handleVolumeChange}
                              />
                            </div>
                          </div>
                        )}
                      </div>

                      <Tooltip content="Fullscreen (F)" placement="bottom">
                        <Button
                          isIconOnly
                          aria-label="Toggle fullscreen"
                          className="text-white hover:bg-white/10"
                          size="sm"
                          variant="light"
                          onPress={toggleFullscreen}
                        >
                          <Maximize2 className="h-5 w-5" />
                        </Button>
                      </Tooltip>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="absolute inset-0 grid place-items-center">
              <div className="h-10 w-10 animate-spin rounded-full border-2 border-white/30 border-t-transparent" />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
